import {
	computed, observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { GetLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { PACKAGE_PERMISSIONS, USER_INFO } from '../constants/storage-keys';
import { navigate } from 'gatsby';
import { CURRENT_PLATFORM } from '../constants/settings';
import { Gender, PackageType, PAGE_SIZE, VmList, VmListT } from '../constants/options';
import { ProfileTemplate } from '../utilities/profile';
import { GetApiLang, GetLangKey, GetUserProfile, IsClinicPackageIdentical, IsTwoListsIdentical, Language } from '../utilities/general';
import { IsZero } from "../utilities/field-validation";
import { PACKAGE_END_DATA_NULL_PLACEHOLDER } from '../utilities/package-subscription';
import { useIsDesktop } from '../hooks/use-is-desktop';

export default class PackageStore {

	loading: boolean = false;
	loadingFeatures: boolean = false;
	loadingPackages: boolean = false;
	loadingPermissions: boolean = false;
	//   loadingHistory: boolean = false;

	packagePermissions: any = null;
	permissionChanged: boolean = false;

	creating: boolean = false;

	updating: boolean = false;

	//   processingHistory: boolean = false;

	rootStore: RootStore;
	loadingPayment: boolean = false;

	constructor(rootStore: RootStore) {
		makeObservable(this, {
			loading: observable,
			loadingFeatures: observable,
			creating: observable,
			updating: observable,
			loadingPackages: observable,
			loadingPayment: observable,
			loadingPermissions: observable,
			packagePermissions: observable,
			permissionChanged: observable,
		});
		this.rootStore = rootStore;
	}

	searchFeature = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = Language.ENGLISH) => {
		this.loadingFeatures = true;
		try {
			const res = await Axios.post(`/api/Package/Feature/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingFeatures = false;
		}
	};

	searchPackage = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}, lang: Language = Language.ENGLISH) => {
		this.loadingPackages = true;
		try {
			const res = await Axios.post<VmListT<PackageType>>(`/api/Package/Search/${skip}/${take}?lang=${lang}&platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPackages = false;
		}
	};



	getPackage = async (id: number, lang: Language = Language.ENGLISH) => {
		this.loading = true;
		try {
			const res = await Axios.get(`/api/Package/${id}?lang=${lang}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	searchMemberPackage = async (skip: number, take: number, reqModel: any = {}) => {
		this.loadingPackages = true;
		try {
			const res = await Axios.post(`/api/Package/Practitioner/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPackages = false;
		}
	};

	setPermissionChanged = (changed: boolean) => this.permissionChanged = changed;
	getMemberPackagePermission = async (user?: any) => {
		if (this.loadingPermissions && !user) return;
		this.loadingPermissions = true;
		this.permissionChanged = false;
		try {
			const userInfo = user ?? GetUserProfile();
			const isMini = isWeb && location.pathname.includes("/mini");
			const res = await Axios.get(`/api/Package/Permission?memberId=${userInfo.id}&platformId=${CURRENT_PLATFORM}`);

			const newPermission = structuredClone(res.data);
			const currentClinicId = structuredClone(userInfo.currentClinicId); 
			const currentClinic = structuredClone(userInfo.availableClinics.find((ac: any) => ac.id === currentClinicId));
			
			const hadOldPermission = userInfo.packagePermissions?.endDate !== PACKAGE_END_DATA_NULL_PLACEHOLDER;
			if (hadOldPermission) {
				console.log("[Permisison] updated start", userInfo, userInfo.packagePermissions, currentClinic);
				if (IsZero(currentClinicId) || !currentClinic) userInfo.currentClinicId = 0;
				else {
					// 对比新老Permission里当前诊所的包存不存在 
					const clinicPackage = newPermission.clinicPackages.find((c: any) => c.clinicId == currentClinicId);
					const oldClinicPackage = userInfo.packagePermissions.clinicPackages.find((c: any) => c.clinicId == currentClinicId);
					console.log("[Permisison] updated", clinicPackage, oldClinicPackage);
					if (!IsClinicPackageIdentical(clinicPackage, oldClinicPackage) && (!currentClinic?.isOwner || isMini)) this.setPermissionChanged(true);
				}
			}
			userInfo.packagePermissions = res.data;
			console.log("[Permisison] updated end", userInfo);
			SetLocalStorage(USER_INFO, userInfo);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPermissions = false;
		}
	};

	searchPackagePayment = async (skip: number, take: number = PAGE_SIZE, reqModel: any = {}) => {
		this.loadingPayment = true;
		try {
			const res = await Axios.post(`/api/Package/Payment/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPayment = false;
		}
	};

	createAddonToClinicPackage = async (req: any) => {
		this.loadingPackages = true;
		try {
			const res = await Axios.post(`/api/Clinic/Package/Addon?lang=${GetApiLang()}&platformId=${CURRENT_PLATFORM}`, req);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPackages = false;
		}
	};

	getPaymentCards = async () => {
		this.loadingPayment = true;
		try {
			const res = await Axios.get(`/api/Payment/Card/Search?lang=${GetApiLang()}&platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPayment = false;
		}
	};

	disableCardById = async (cardId: string) => {
		this.loadingPayment = true;
		try {
			const res = await Axios.post(`/api/Payment/Card/Disable/${cardId}?lang=${GetApiLang()}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPayment = false;
		}
	};

	togglePackageAutoRecharge = async (clinicPackageId: number, isAutoTopUp: boolean) => {
		this.loadingPackages = true;
		try {
			let url = `/api/Clinic/Package/AutoTopUp/${clinicPackageId}/`;
			if(isAutoTopUp) url += `SwitchOn`;
			else url += `SwitchOff`;
			const res = await Axios.put(url);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPackages = false;
		}
	};

	togglePersonalPackageAutoRecharge = async (isTopUp: boolean) => {
		this.loadingPackages = true;
		try {
			const url = `/api/Clinic/Personal/Package/AutoTopUp/${isTopUp ? "SwitchOn" : "SwitchOff"}`;
			const res = await Axios.put(url);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPackages = false;
		}
	};

	saveCard = async (req: any) => {
		this.loadingPayment = true;
		try {
			const res = await Axios.post(`/api/Payment/Card`, req);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPayment = false;
		}
	};

	toggleClinicPackageSubscription = async (clinicPackageId: number, isSwitchOn: boolean) => {
		this.loadingPackages = true;
		try {
			const url = `/api/Clinic/Package/Subscription/${clinicPackageId}/${isSwitchOn ? "SwitchOn" : "SwitchOff"}`;
			const res = await Axios.put(url);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingPackages = false;
		}
	};

};